import { useState, useEffect, useRef } from "react";
import ReactDom from "react-dom";
import ICON_CLOSE_X_2 from "../../Assets/jsx/ICON_CLOSE_X_2";

const modalRoot = document.getElementById("modal-root");

const Modal = ({
  id = "",
  modalClass = "",
  modalSize = "md",
  onClose,
  isOpen,
  is_hide,
  children,
  title = "",
  disableBackHijack = false,
  ...restProps
}) => {
  const [fadeType, setFadeType] = useState(null);
  const background = useRef();
  const isClosingFromBackButton = useRef(false);
  const [bodyHeight, setBodyHeight] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef(null);
  const timeoutRef = useRef(null);

  const transitionEnd = (e) => {
    if (e.propertyName !== "opacity" || fadeType === "in") return;
    if (fadeType === "out") {
      onClose();
    }
  };

  const onEscKeyDown = (e) => {
    if (e.key !== "Escape") return;
    setFadeType("out");
  };

  const handleClick = (e) => {
    e.preventDefault();
    setFadeType("out");
  };

  useEffect(() => {
    window.addEventListener("keydown", onEscKeyDown, false);
    setTimeout(() => setFadeType("in"), 0);

    const handlePopState = () => {
      isClosingFromBackButton.current = true;
      setFadeType("out");
    };

    // Only hijack the back button if the parent component hasn't disabled it
    if (!disableBackHijack) {
      window.history.pushState(null, null, window.location.href);
      window.addEventListener("popstate", handlePopState);
    }

    return () => {
      window.removeEventListener("keydown", onEscKeyDown, false);

      if (!disableBackHijack) {
        window.removeEventListener("popstate", handlePopState);

        // 🚨 REMOVED window.history.back() HERE 🚨
        // We leave the dummy state in the history stack. It prevents React Router
        // from accidentally triggering a real page navigation when the modal closes.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFadeType("out");
  }, [isOpen]);

  // --- Smooth Height Observer Hook ---
  useEffect(() => {
    if (!contentRef.current) return;

    const observer = new ResizeObserver((entries) => {
      // requestAnimationFrame prevents "ResizeObserver loop limit exceeded" errors
      window.requestAnimationFrame(() => {
        if (contentRef.current) {
          const newHeight = contentRef.current.offsetHeight;
          setBodyHeight((prev) => {
            // Only trigger animation if we are transitioning from an already established height
            if (prev !== null && prev !== newHeight) {
              setIsAnimating(true);
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
              timeoutRef.current = setTimeout(() => setIsAnimating(false), 300);
            }
            return newHeight;
          });
        }
      });
    });

    observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getModalWidth = () => {
    switch (modalSize) {
      case "large":
        return "1200px";
      case "small":
        return "450px";
      case "medium":
        return "900px";
      case "full":
        return "calc( 100vw - 20px )";
      default:
        return "480px";
    }
  };

  const getModalMaxWidth = () => {
    switch (modalSize) {
      case "full":
        return "calc( 100vw - 20px )";
      default:
        return "calc( 100vw - 20px )";
    }
  };

  const getModalMaxHeight = () => {
    if (modalSize === "full") {
      if (is_hide) {
        return "calc( 100svh - 20px )";
      } else {
        return "100svh";
      }
    } else {
      return "calc( 100svh - 100px )";
    }
  };

  const getBoxBodyPadding = () => {
    switch (modalSize) {
      case "full":
        return "0px";
      default:
        return "15px";
    }
  };

  const getBoxBodyPaddingBottom = () => {
    switch (modalSize) {
      case "full":
        return "0px";
      default:
        return "15px";
    }
  };

  const wrapperStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: fadeType === "in" ? 1 : 0,
    zIndex: 4,
    width: getModalWidth(),
    maxWidth: getModalMaxWidth(),
    maxHeight: getModalMaxHeight(),
    boxSizing: "border-box",
    margin: "auto",
    WebkitTransition: "0.01s",
    MozTransition: "0.01s",
    OTransition: "0.01s",
    transition: "0.01s",
  };

  const backgroundStyles = {
    background: "rgba(255, 255, 255, 0)",
    position: "fixed",
    display: "block",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    outline: 0,
  };

  const boxDialogStyles = {
    borderRadius: "24px",
    width: "100%",
    maxHeight: "100%",
    height: modalSize === "full" ? "calc(100svh - 20px)" : "auto",
    position: "relative",
    backgroundColor: "#ffffff75",
    backdropFilter: "blur(7px)",
    WebkitBackdropFilter: "blur(7px)",
    border: "2px solid #0000001f",
    display: "flex",
    flexDirection: "column",
    padding: "0px 12px 12px 12px",
    boxSizing: "border-box",
  };

  const boxHeaderStyles = {
    padding: "12px 0px 12px 0px",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  };

  const titleStyles = {
    fontWeight: 600,
    fontSize: "15px",
    display: "flex",
    alignItems: "center",
    margin: 0,
    backgroundColor: "#fff",
    borderRadius: "14px",
    padding: "5px 10px",
  };

  const closeButtonStyles = {
    position: "relative",
    cursor: "pointer",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0px",
  };

  // Dynamically calculate padding to safely inject into the animated flex-basis
  const padTop = parseInt(getBoxBodyPadding(), 10) || 0;
  const padBottom = parseInt(getBoxBodyPaddingBottom(), 10) || 0;
  const verticalPadding = padTop + padBottom;
  const isDynamicHeight = modalSize !== "full";

  const boxBodyStyles = {
    boxSizing: "border-box",
    // Hide scrollbar momentarily while animating to prevent an ugly scrollbar flash
    overflowY: isAnimating ? "hidden" : "auto",
    overflowX: "hidden",
    padding: getBoxBodyPadding(),
    paddingBottom: getBoxBodyPaddingBottom(),
    position: "relative",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    // Drive height via flex-basis. If null (initial load), let it render naturally.
    // Otherwise, strictly force the dimension including internal paddings.
    flex: isDynamicHeight
      ? bodyHeight === null
        ? "1 1 auto"
        : `0 1 ${bodyHeight + verticalPadding}px`
      : "1",
    minHeight: 0,
    transition: "flex-basis 0.3s ease-in-out",
    boxShadow: "0 0 40px rgba(0, 0, 0, 0.06), 0 0 12px rgba(0, 0, 0, 0.04)",
  };

  return ReactDom.createPortal(
    <div
      id={id}
      className={`wrapper size-${modalSize} fade-${fadeType} ${modalClass}`}
      role="dialog"
      style={wrapperStyles}
      onTransitionEnd={transitionEnd}
      {...restProps}
    >
      <div
        style={backgroundStyles}
        onMouseDown={handleClick}
        ref={background}
      />

      <div style={boxDialogStyles}>
        {(title || is_hide !== "hide") && (
          <div style={boxHeaderStyles}>
            {title && <div style={titleStyles}>{title}</div>}
            {is_hide !== "hide" ? (
              <button
                onClick={handleClick}
                style={closeButtonStyles}
                className="close_button background_black"
                data-mapid="clickTutup"
              >
                <ICON_CLOSE_X_2 color="#fff" style={{ width: "15px" }} />
              </button>
            ) : null}
          </div>
        )}
        <div
          style={{
            marginTop: title || is_hide !== "hide" ? 0 : "12px",
            ...boxBodyStyles,
          }}
        >
          <div
            ref={contentRef}
            style={{ display: "flex", flexDirection: "column" }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    modalRoot,
  );
};

export default Modal;
